import { each } from 'lodash-es';

import * as angular from 'angular';

export interface CategorySpend {
  categoryPotentialSpend: number;
  continuedSpend: number;
  last12monthSpend: number;
  categorySavings: number;
  next12monthSpend: number;
  crossReferenceSpend: number;
  spendWithSeller: number;
}

export interface CategoryAnalytics {
  allPercent: number;
  spend: number;
  allPotentialSavings: number;
  potentialSavings: number;
  percent: number;
  categoryName: string;
  categoryId: number;
}

interface FacilityParent {
  id: number;
  level: number;
  name: string;
}

export interface Facility extends Profile {
  parents: FacilityParent[];
  children: Facility[];
  savings: number;
  existingOffers: string;
  includedInRfp: boolean;
  categorySpend: CategorySpend;
}

export interface Profile {
  address1: string;
  address2: string;
  city: string;
  gln: string;
  id: number;
  name: string;
  state: string;
  zip: string;
}

export interface MemberImpact {
  memberId: number;
  memberName: string;
  memberPlanId: number;
  sellerImpact: SellerImpact[];
}

export interface SellerImpact {
  sellerId: number;
  continuedSpend: number;
  additionalValue: number;
  plannedSpend: number;
  potentialSpend: number;
  savings: number;
  marketShare: number;
  spendBenchmark: {
    continuedSpend: number;
    plannedSpend: number;
    continuedSimilarBuyersBenchmark: number;
    plannedSimilarBuyersBenchmark: number;
    continuedAllBuyersBenchmark: number;
    plannedAllBuyersBenchmark: number;
  };
  continuedBenchmarkAll: number;
  plannedBenchmarkAll: number;
  continuedBenchmarkLikeMe: number;
  plannedBenchmarkLikeMe: number;
  conversionsNeededCount: number;
  inclusionStatus: string;
  inclusionModifiedBy: any;
  priceType: string;
  duration: number;
  targetMarketShare?: any;
  meetsTargetMarketShare?: any;
}

export default class BuyersService {
  static $inject = ['$http'];

  baseUrl = '/aptitude/api/buyers/';

  constructor(private $http) {
    'ngInject';
  }

  getBuyers(): ng.IPromise<any> {
    return this.$http.get(this.baseUrl);
  }

  /**
   *
   * @param  {number}           buyerId
   * @param  {number}           rfpId    Is only valid post draft state
   * @param  {number}           sellerId
   * @return {ng.IPromise<any>}
   */
  getCategories(
    buyerId: number,
    rfpId?: number,
    sellerId?: number
  ): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}${buyerId}/categories`, {
      params: {
        rfpId,
        sellerId
      }
    });
  }

  getCategoryAnalytics(buyerId: number): ng.IPromise<CategoryAnalytics[]> {
    return this.$http.get(`${this.baseUrl}${buyerId}/categoryAnalytics`);
  }

  getFacilitiesCount(buyerId: number, rfpId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}${buyerId}/facilitiescount`, {
      params: {
        rfpId
      }
    });
  }

  // TODO: Stop using this function in favor of getFacilities.
  getMyFacilitiesFlat(
    rfpId: number,
    isGroupRequest: boolean
  ): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}me/facilities`, {
      params: {
        rfpId,
        isGroupRequest,
        flattenHierarchy: true
      }
    });
  }

  getFacilities(
    buyerId: number,
    categoryId: number,
    rfpId: number,
    sellerId: number
  ): ng.IPromise<Facility[]> {
    return this.$http.get(`${this.baseUrl}${buyerId}/facilities`, {
      params: {
        categoryId,
        isGroupRequest: false,
        rfpId,
        expand: 'savings',
        flattenHierarchy: true,
        sellerId,
        isPrimaryOrg: false
      }
    });
  }

  getFacilitiesForSeller(
    memberId: number,
    categoryId: number,
    rfpId: number,
    sellerId: number,
    priceType: string,
    duration: number,
    marketShare
  ): ng.IPromise<any> {
    var fixNullCategorySpendNumbers = function (facility) {
      var categorySpend = facility.categorySpend;

      categorySpend.categoryPotentialSpend =
        categorySpend.categoryPotentialSpend || 0;
      categorySpend.categoryPotentialSpend =
        categorySpend.categoryPotentialSpend || 0;
      categorySpend.categorySavings = categorySpend.categorySavings || 0;
      categorySpend.crossReferenceSpend =
        categorySpend.crossReferenceSpend || 0;
      categorySpend.last12monthSpend = categorySpend.last12monthSpend || 0;
      categorySpend.next12monthSpend = categorySpend.next12monthSpend || 0;
      categorySpend.spendWithSeller = categorySpend.spendWithSeller || 0;
    };

    return this.$http
      .get(`${this.baseUrl}${memberId}/facilities`, {
        params: {
          categoryId,
          isGroupRequest: false,
          rfpId,
          expand: 'savings',
          flattenHierarchy: true,
          priceType,
          duration,
          marketShare,
          sellerId
        }
      })
      .then(function (results) {
        // Update numbers on the root
        fixNullCategorySpendNumbers(results[0]);

        each(results[0].children, fixNullCategorySpendNumbers);

        return results;
      });
  }

  spendByFacilityOrSystem(
    buyerId: number,
    categoryId: number,
    isGroupRequest: boolean,
    rfpId: number,
    sellerId: number,
    { cache = false } = {}
  ): ng.IPromise<Facility[]> {
    return this.$http.get(`${this.baseUrl}${buyerId}/facilities`, {
      cache,
      params: {
        categoryId,
        flattenHierarchy: true,
        isGroupRequest,
        rfpId,
        sellerId
      }
    });
  }

  // TODO: Get Kemi to move it to the api service - hopefully this still belongs here
  getMemberImpact(
    memberId: number,
    masterPlanId: number
  ): ng.IPromise<MemberImpact> {
    return this.$http.get(
      '../buyer/GroupImpactServiceController/getMemberImpact',
      {
        params: {
          masterPlanId,
          memberId
        },
        intercept: true
      }
    );
  }

  // Retrieves partners that are available for report downloading
  getPartnersWhoCanDownloadReports(buyerId: number) {
    return this.$http.get(`${this.baseUrl}${buyerId}/recruitment`);
  }

  getPurchaseHistory(requestId) {
    return this.$http.get('/aptitude/api/buyers/me/purchasehistory', {
      params: {
        rfpId: requestId
      }
    });
  }
}
